// Recipe Module Phase 2 (docs/RECIPE_MODULE.md) — run a dough recipe: pick whichever input mode
// matches what you actually know today (number of dough balls, total dough weight, or total flour
// weight — the original standalone configurator only ever supported ball count), enter it in g/kg/
// lb, and get the full absolute breakdown instantly, split into three clearly separated stages —
// Total recipe quantities, the pre-ferment's own quantities (if one is configured), and what to mix
// into the Final dough the next day — since knowing the biga's amounts alone isn't enough to
// actually build the dough. Logs a RecipeExecutionLog row for accountability, same as
// PercentageRecipeRunPage. Printable (🖨️ button) for handing a paper copy to factory staff who
// don't have platform access.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import recipeService from '../services/recipeService';
import LotUsageRow from '../components/LotUsageRow';
import { useLanguage, t } from '../context/LanguageContext';
import { PRE_FERMENT_LABELS, YEAST_TYPE_OPTIONS, PRE_FERMENT_CARRIES_YEAST } from '../config/doughLabels';

const LB_TO_KG = 0.453592;
const toGrams = (value, unit) => {
  const n = parseFloat(value);
  if (isNaN(n)) return NaN;
  if (unit === 'kg') return n * 1000;
  if (unit === 'lb') return n * LB_TO_KG * 1000;
  return n; // g
};

function WeightUnitSelect({ value, onChange }) {
  return (
    <select className="form-select" style={{ width: 90, flex: '0 0 auto' }} value={value} onChange={e => onChange(e.target.value)}>
      <option value="g">g</option>
      <option value="kg">kg</option>
      <option value="lb">lb</option>
    </select>
  );
}

const INPUT_MODES = [
  { value: 'ball_count', en: 'Number of dough balls', es: 'Número de bolas de masa' },
  { value: 'total_dough_weight', en: 'Total dough weight', es: 'Peso total de la masa' },
  { value: 'total_flour_weight', en: 'Total flour weight', es: 'Peso total de harina' },
];

// Baker's-%-of-total-flour badge — muted gray text (not color-only) so it still reads correctly
// printed in black & white.
function PctBadge({ value }) {
  if (value == null || isNaN(value)) return null;
  return <span className="text-muted ms-1" style={{ fontSize: 11 }}>({Number(value).toFixed(value < 10 ? 2 : 1)}%)</span>;
}

// Renders the three-stage breakdown (Total / Pre-ferment / Final dough) shared by both the live
// "Weigh out" preview (before logging a run) and the post-run result — same data shape either way.
// `showPercent` toggles baker's-percentage badges (always % of TOTAL flour, except the
// pre-ferment's own water, which is shown as its own internal hydration % — the number a baker
// actually cares about for that sub-formula).
function BreakdownSections({ b, recipe, lang, fmtG, showPercent, onLotUsageChange }) {
  const preFermentLabel = recipe.preFermentType !== 'none' ? PRE_FERMENT_LABELS[recipe.preFermentType] || recipe.preFermentType : null;
  const yeastLabel = YEAST_TYPE_OPTIONS.find(o => o.value === recipe.yeastType)?.label || t('Yeast', 'Levadura', lang);
  const pct = showPercent ? (v) => <PctBadge value={v} /> : () => null;
  const pctOfFlour = showPercent ? (g) => <PctBadge value={(g / b.totalFlourG) * 100} /> : () => null;

  return (
    <>
      <div className="text-uppercase text-muted mb-1 fw-bold" style={{ fontSize: 11, letterSpacing: 1 }}>
        📦 {t('Total recipe quantities', 'Cantidades totales de la receta', lang)}
      </div>
      <table className="table table-sm mb-3">
        <tbody>
          <tr><td className="fw-semibold">{t('Total flour', 'Harina total', lang)}</td><td className="text-end fw-semibold">{fmtG(b.totalFlourG)}</td></tr>
          {b.flourBreakdown.map(l => (
            <tr key={l.ingredientId}>
              <td className="ps-3 text-muted">
                {l.ingredientName}{pct(l.percentOfFlour)}
                {onLotUsageChange && <LotUsageRow ingredientId={l.ingredientId} expectedKg={l.quantityG / 1000} onChange={usages => onLotUsageChange(l.ingredientId, usages)} />}
              </td>
              <td className="text-end">{fmtG(l.quantityG)}</td>
            </tr>
          ))}
          <tr><td>{t('Water', 'Agua', lang)}{pct(recipe.hydrationPercent)}</td><td className="text-end">{fmtG(b.waterG)}</td></tr>
          <tr>
            <td>
              {t('Salt', 'Sal', lang)}{pct(recipe.saltPercent)}
              {onLotUsageChange && recipe.saltIngredientId && <LotUsageRow ingredientId={recipe.saltIngredientId} expectedKg={b.saltG / 1000} onChange={usages => onLotUsageChange(recipe.saltIngredientId, usages)} />}
            </td>
            <td className="text-end">{fmtG(b.saltG)}</td>
          </tr>
          <tr>
            <td>
              {yeastLabel}{pct(recipe.yeastPercent)}
              {onLotUsageChange && recipe.yeastIngredientId && <LotUsageRow ingredientId={recipe.yeastIngredientId} expectedKg={b.yeastG / 1000} onChange={usages => onLotUsageChange(recipe.yeastIngredientId, usages)} />}
            </td>
            <td className="text-end">{fmtG(b.yeastG)}</td>
          </tr>
          {b.extraBreakdown.map((e, idx) => (
            <tr key={idx}>
              <td>
                {e.label}{pct(e.percentOfFlour)}
                {onLotUsageChange && e.ingredientId && <LotUsageRow ingredientId={e.ingredientId} expectedKg={e.quantityG / 1000} onChange={usages => onLotUsageChange(e.ingredientId, usages)} />}
              </td>
              <td className="text-end">{fmtG(e.quantityG)}</td>
            </tr>
          ))}
          <tr className="table-light"><td className="fw-semibold">{t('Total dough', 'Masa total', lang)}</td><td className="text-end fw-semibold">{fmtG(b.doughG)}</td></tr>
        </tbody>
      </table>

      {b.preFerment && (
        <>
          <div className="text-uppercase mb-1 fw-bold" style={{ fontSize: 11, letterSpacing: 1, color: '#fd7e14' }}>
            🫙 {preFermentLabel} {t('quantity', 'cantidad', lang)}
          </div>
          <table className="table table-sm mb-3">
            <tbody>
              <tr><td>{t('Flour', 'Harina', lang)}{pct(recipe.preFermentFlourPercent)}</td><td className="text-end">{fmtG(b.preFerment.flourG)}</td></tr>
              <tr><td>{t('Water', 'Agua', lang)}{pct(recipe.preFermentHydrationPercent)}</td><td className="text-end">{fmtG(b.preFerment.waterG)}</td></tr>
              {recipe.preFermentType !== 'autolyse' && (
                <tr><td>{yeastLabel} ({t('all of it', 'toda', lang)}){pct(recipe.yeastPercent)}</td><td className="text-end">{fmtG(b.preFerment.yeastG)}</td></tr>
              )}
              {b.preFerment.extraIngredients.map((e, idx) => (
                <tr key={idx}><td>{e.label}{pct(e.percentOfFlour)}</td><td className="text-end">{fmtG(e.quantityG)}</td></tr>
              ))}
            </tbody>
          </table>

          <div className="text-uppercase mb-1 fw-bold" style={{ fontSize: 11, letterSpacing: 1, color: '#198754' }}>
            🍞 {t('Final dough quantity', 'Cantidad de masa final', lang)}
          </div>
          <table className="table table-sm mb-3">
            <tbody>
              <tr><td>{t('Flour', 'Harina', lang)}{pctOfFlour(b.finalFlourG)}</td><td className="text-end">{fmtG(b.finalFlourG)}</td></tr>
              <tr><td>{t('Water', 'Agua', lang)}{pctOfFlour(b.finalWaterG)}</td><td className="text-end">{fmtG(b.finalWaterG)}</td></tr>
              <tr><td>{t('Salt', 'Sal', lang)}{pct(recipe.saltPercent)}</td><td className="text-end">{fmtG(b.saltG)}</td></tr>
              {recipe.preFermentType === 'autolyse' && (
                <tr><td>{yeastLabel} ({t('all of it', 'toda', lang)}){pct(recipe.yeastPercent)}</td><td className="text-end">{fmtG(b.finalYeastG)}</td></tr>
              )}
              {b.finalExtraBreakdown.map((e, idx) => (
                <tr key={idx}><td>{e.label}{pct(e.percentOfFlour)}</td><td className="text-end">{fmtG(e.quantityG)}</td></tr>
              ))}
              <tr className="table-light"><td className="fw-semibold">{t('+ all of the', '+ toda la', lang)} {preFermentLabel}</td><td className="text-end fw-semibold">{fmtG(b.preFerment.flourG + b.preFerment.waterG + b.preFerment.yeastG + b.preFerment.extraIngredients.reduce((s, e) => s + e.quantityG, 0))}</td></tr>
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

export default function DoughRecipeRunPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [inputMode, setInputMode] = useState('ball_count');
  const [ballCount, setBallCount] = useState('');
  const [ballWeightG, setBallWeightG] = useState('');
  const [weightValue, setWeightValue] = useState('');
  const [weightUnit, setWeightUnit] = useState('g');

  const [checkedSteps, setCheckedSteps] = useState({});
  const [notes, setNotes] = useState('');
  const [outputLotCode, setOutputLotCode] = useState('');
  const [lotUsagesMap, setLotUsagesMap] = useState({}); // ingredientId -> [{lotCode, quantityKg}]
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showPercent, setShowPercent] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await recipeService.getDoughRecipe(id);
      setRecipe(data);
      if (data.defaultBallWeightG) setBallWeightG(String(data.defaultBallWeightG));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load recipe.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = async () => {
    setShowHistory(true);
    try {
      const data = await recipeService.getDoughRecipeExecutions(id);
      setHistory(Array.isArray(data) ? data : []);
    } catch { setHistory([]); }
  };

  const totalPercent = useMemo(() => {
    if (!recipe) return 100;
    const extraPercent = (recipe.extraIngredients || []).reduce((s, e) => s + Number(e.percentOfFlour || 0), 0);
    return 100 + recipe.hydrationPercent + recipe.saltPercent + recipe.yeastPercent + extraPercent;
  }, [recipe]);

  const totalFlourG = useMemo(() => {
    if (!recipe) return NaN;
    if (inputMode === 'total_flour_weight') return toGrams(weightValue, weightUnit);
    if (inputMode === 'total_dough_weight') return toGrams(weightValue, weightUnit) * 100 / totalPercent;
    const count = parseFloat(ballCount);
    const weight = parseFloat(ballWeightG);
    return (count * weight) * 100 / totalPercent;
  }, [recipe, inputMode, weightValue, weightUnit, ballCount, ballWeightG, totalPercent]);

  const validInput = recipe && !isNaN(totalFlourG) && totalFlourG > 0
    && (inputMode === 'ball_count'
      ? (!!ballCount && parseFloat(ballCount) > 0 && !!ballWeightG && parseFloat(ballWeightG) > 0)
      : (!!weightValue && parseFloat(weightValue) > 0));

  // Mirrors the backend's computeDoughBreakdown() exactly (routes/recipeRoutes.js), including the
  // pre-ferment-vs-final-dough extra-ingredient phase split.
  const preview = useMemo(() => {
    if (!recipe || !validInput) return null;
    const hydration = recipe.hydrationPercent, salt = recipe.saltPercent, yeast = recipe.yeastPercent;
    const doughG = totalFlourG * totalPercent / 100;
    const waterG = totalFlourG * hydration / 100;
    const saltG = totalFlourG * salt / 100;
    const yeastG = totalFlourG * yeast / 100;
    const flourBreakdown = recipe.flourLines.map(l => ({ ...l, quantityG: totalFlourG * l.percentOfFlour / 100 }));
    const extraBreakdown = (recipe.extraIngredients || []).map(e => ({
      ...e,
      label: e.label || e.ingredientName || 'Ingredient',
      quantityG: totalFlourG * e.percentOfFlour / 100,
    }));

    let preFerment = null;
    let finalFlourG = totalFlourG, finalWaterG = waterG, finalYeastG = 0;
    let finalExtraBreakdown = extraBreakdown;
    if (recipe.preFermentType !== 'none' && recipe.preFermentFlourPercent) {
      const pfFlourG = totalFlourG * recipe.preFermentFlourPercent / 100;
      const pfWaterG = pfFlourG * (recipe.preFermentHydrationPercent || 0) / 100;
      const carriesYeast = PRE_FERMENT_CARRIES_YEAST[recipe.preFermentType] !== false;
      preFerment = { flourG: pfFlourG, waterG: pfWaterG, yeastG: carriesYeast ? yeastG : 0, extraIngredients: extraBreakdown.filter(e => e.phase === 'preferment') };
      finalFlourG = totalFlourG - pfFlourG;
      finalWaterG = waterG - pfWaterG;
      finalYeastG = carriesYeast ? 0 : yeastG;
      finalExtraBreakdown = extraBreakdown.filter(e => e.phase !== 'preferment');
    }
    return { totalFlourG, doughG, waterG, saltG, yeastG, flourBreakdown, extraBreakdown, finalExtraBreakdown, preFerment, finalFlourG, finalWaterG, finalYeastG };
  }, [recipe, validInput, totalFlourG, totalPercent]);

  const toggleStep = (stepId) => setCheckedSteps(prev => ({ ...prev, [stepId]: !prev[stepId] }));
  const allStepsChecked = recipe && recipe.steps.length > 0 ? recipe.steps.every(s => checkedSteps[s.id]) : true;
  const stepText = (s) => (lang === 'es' && s.instructionEs ? s.instructionEs : s.instruction);

  const handleSubmit = async () => {
    if (!validInput) return;
    setSubmitting(true);
    setError('');
    try {
      const lotUsages = Object.entries(lotUsagesMap).flatMap(([ingredientId, usages]) =>
        usages.map(u => ({ ingredientId: Number(ingredientId), lotCode: u.lotCode, quantityKg: Number(u.quantityKg) })));
      const payload = {
        inputMode,
        checkedSteps: Object.keys(checkedSteps).filter(k => checkedSteps[k]).map(Number),
        notes: notes.trim() || null,
        outputLotCode: outputLotCode.trim() || undefined,
        lotUsages,
      };
      if (inputMode === 'ball_count') {
        payload.ballCount = Number(ballCount);
        payload.ballWeightG = Number(ballWeightG);
      } else if (inputMode === 'total_dough_weight') {
        payload.totalDoughWeightG = toGrams(weightValue, weightUnit);
      } else {
        payload.totalFlourWeightG = toGrams(weightValue, weightUnit);
      }
      const data = await recipeService.executeDoughRecipe(id, payload);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to log this run.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRunAgain = () => {
    setResult(null);
    setBallCount('');
    setWeightValue('');
    setCheckedSteps({});
    setNotes('');
    setOutputLotCode('');
    setLotUsagesMap({});
  };

  const fmtG = (g) => (g >= 1000 ? `${(g / 1000).toFixed(3)} kg` : `${g.toFixed(1)} g`);

  // What the user actually asked to make — shown as a subheader so a printed sheet is never
  // "blind" about the batch it belongs to.
  const batchSummary = useMemo(() => {
    if (inputMode === 'ball_count' && ballCount && ballWeightG) {
      return `${ballCount} ${t('balls', 'bolas', lang)} × ${ballWeightG} g`;
    }
    if (weightValue) {
      const modeLabel = inputMode === 'total_dough_weight' ? t('Total dough', 'Masa total', lang) : t('Total flour', 'Harina total', lang);
      return `${modeLabel}: ${weightValue} ${weightUnit}`;
    }
    return null;
  }, [inputMode, ballCount, ballWeightG, weightValue, weightUnit, lang]);

  if (loading) {
    return (
      <div className="container mt-4 text-center text-muted py-5">
        <div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}
      </div>
    );
  }
  if (error && !recipe) {
    return <div className="container mt-4"><div className="alert alert-danger">{error}</div></div>;
  }

  const CARD_STYLE = (color) => ({ border: '1px solid #e9ecef', borderTopWidth: 3, borderTopStyle: 'solid', borderTopColor: color });
  const SECTION_HEADER_STYLE = { fontSize: 11, letterSpacing: 1, fontWeight: 700 };

  return (
    <div className="container mt-4 pb-5">
      <style>{`
        @media print {
          .drp-no-print { display: none !important; }
          .card, .card-body { box-shadow: none !important; border: none !important; background: #fff !important; }
          body, .container { background: #fff !important; }
        }
      `}</style>

      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-start align-items-md-center mb-4 pb-3 gap-2 drp-no-print" style={{ borderBottom: '2px solid #f1f3f5' }}>
        <div>
          <h4 className="mb-1" style={{ color: '#198754' }}>▶ 🍞 {recipe.name}</h4>
          {recipe.category && <small className="text-muted">{recipe.category}</small>}
          {batchSummary && <div className="text-muted small fw-semibold">{t('Batch', 'Lote', lang)}: {batchSummary}</div>}
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/recipes')}>
          {t('← Back to Library', '← Volver a la biblioteca', lang)}
        </button>
      </div>

      <div className="d-none d-print-block mb-3">
        <h4 className="mb-0">🍞 {recipe.name}</h4>
        {recipe.category && <small className="text-muted">{recipe.category}</small>}
        {batchSummary && <div className="fw-semibold">{t('Batch', 'Lote', lang)}: {batchSummary}</div>}
      </div>

      {error && <div className="alert alert-danger py-2 drp-no-print">{error}</div>}

      {result ? (
        <div className="card mb-3" style={CARD_STYLE('#198754')}>
          <div className="card-body">
            <div className="text-center mb-3 drp-no-print">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2"
                style={{ width: 56, height: 56, background: '#d1f0dd', fontSize: 28 }}>✅</div>
              <h6 className="text-success mb-0">{t('✓ Run logged', '✓ Preparación registrada', lang)}</h6>
            </div>
            {result.outputCredited && (
              <div className="alert alert-info py-2 mb-3 drp-no-print">
                🏷️ {t('Credited to Inventory', 'Acreditado al Inventario', lang)} — {t('lot code', 'código de lote', lang)}: <strong>{result.outputLotCode}</strong>
              </div>
            )}
            <div className="form-check form-switch mb-2 drp-no-print">
              <input type="checkbox" className="form-check-input" id="showPercentResult" checked={showPercent} onChange={e => setShowPercent(e.target.checked)} />
              <label className="form-check-label small" htmlFor="showPercentResult">{t("Show baker's %", 'Mostrar % panadero', lang)}</label>
            </div>
            <BreakdownSections
              b={{ totalFlourG: result.totalFlourG, doughG: result.totalDoughG, waterG: result.waterG, saltG: result.saltG, yeastG: result.yeastG,
                flourBreakdown: result.flourBreakdown, extraBreakdown: result.extraBreakdown, finalExtraBreakdown: result.finalExtraBreakdown,
                preFerment: result.preFerment, finalFlourG: result.finalFlourG, finalWaterG: result.finalWaterG, finalYeastG: result.finalYeastG }}
              recipe={recipe} lang={lang} fmtG={fmtG} showPercent={showPercent}
            />
            <div className="fw-semibold mb-3">
              {t('Total cost', 'Costo total', lang)}: {result.totalCost != null ? `$${result.totalCost.toFixed(2)}` : '—'}
            </div>
            {recipe.steps.length > 0 && (
              <div className="mb-3">
                <div className="text-uppercase text-muted mb-2" style={SECTION_HEADER_STYLE}>📋 {t('Steps', 'Pasos', lang)}</div>
                <ol className="mb-0">
                  {recipe.steps.map(s => <li key={s.id} className="mb-1">{stepText(s)}</li>)}
                </ol>
              </div>
            )}
            <div className="d-flex gap-2 drp-no-print">
              <button className="btn btn-primary btn-sm" onClick={handleRunAgain}>{t('Run Again', 'Ejecutar de nuevo', lang)}</button>
              <button className="btn btn-outline-dark btn-sm" onClick={() => window.print()}>🖨️ {t('Print / Save as PDF', 'Imprimir / Guardar como PDF', lang)}</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/recipes')}>{t('Back to Library', 'Volver a la biblioteca', lang)}</button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="card mb-3 drp-no-print" style={CARD_STYLE('#0d6efd')}>
            <div className="card-body">
              <div className="text-uppercase text-muted mb-2" style={SECTION_HEADER_STYLE}>{t('How much do you want to make?', '¿Cuánto quieres hacer?', lang)}</div>
              <div className="btn-group mb-3" role="group">
                {INPUT_MODES.map(m => (
                  <button key={m.value} type="button"
                    className={`btn btn-sm ${inputMode === m.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setInputMode(m.value)}>
                    {t(m.en, m.es, lang)}
                  </button>
                ))}
              </div>

              {inputMode === 'ball_count' ? (
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">{t('Number of balls', 'Número de bolas', lang)} <span className="text-danger">*</span></label>
                    <input type="number" step="1" className="form-control" value={ballCount}
                      onChange={e => setBallCount(e.target.value)} placeholder="e.g. 20" autoFocus />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">{t('Weight per ball (g)', 'Peso por bola (g)', lang)} <span className="text-danger">*</span></label>
                    <input type="number" step="1" className="form-control" value={ballWeightG}
                      onChange={e => setBallWeightG(e.target.value)} placeholder="e.g. 250" />
                  </div>
                </div>
              ) : (
                <div className="row g-3">
                  <div className="col-md-5">
                    <label className="form-label fw-semibold">
                      {inputMode === 'total_dough_weight' ? t('Total dough weight', 'Peso total de la masa', lang) : t('Total flour weight', 'Peso total de harina', lang)} <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input type="number" step="0.01" className="form-control" value={weightValue}
                        onChange={e => setWeightValue(e.target.value)} placeholder="e.g. 5" autoFocus />
                      <WeightUnitSelect value={weightUnit} onChange={setWeightUnit} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {preview && (
            <div className="card mb-3" style={CARD_STYLE('#198754')}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2 drp-no-print">
                  <div className="text-uppercase text-muted" style={SECTION_HEADER_STYLE}>⚖️ {t('Weigh out', 'Pesar', lang)}</div>
                  <div className="d-flex align-items-center gap-3">
                    <div className="form-check form-switch mb-0">
                      <input type="checkbox" className="form-check-input" id="showPercentPreview" checked={showPercent} onChange={e => setShowPercent(e.target.checked)} />
                      <label className="form-check-label small" htmlFor="showPercentPreview">{t("Show baker's %", 'Mostrar % panadero', lang)}</label>
                    </div>
                    <button className="btn btn-outline-dark btn-sm" onClick={() => window.print()}>🖨️ {t('Print / Save as PDF', 'Imprimir / Guardar como PDF', lang)}</button>
                  </div>
                </div>
                <div className="d-none d-print-block text-uppercase text-muted mb-2" style={SECTION_HEADER_STYLE}>⚖️ {t('Weigh out', 'Pesar', lang)}</div>
                <BreakdownSections b={preview} recipe={recipe} lang={lang} fmtG={fmtG} showPercent={showPercent}
                  onLotUsageChange={(ingredientId, usages) => setLotUsagesMap(m => ({ ...m, [ingredientId]: usages }))} />
              </div>
            </div>
          )}

          {recipe.outputIngredientId && (
            <div className="card mb-3 drp-no-print" style={CARD_STYLE('#0dcaf0')}>
              <div className="card-body">
                <div className="text-uppercase text-muted mb-2" style={SECTION_HEADER_STYLE}>🏷️ {t('Traceability', 'Trazabilidad', lang)}</div>
                <label className="form-label small fw-semibold">{t('Output Lot Code (optional)', 'Código de lote de salida (opcional)', lang)}</label>
                <input className="form-control form-control-sm" style={{ maxWidth: 280 }} value={outputLotCode}
                  onChange={e => setOutputLotCode(e.target.value)} placeholder={t('Leave blank to auto-generate', 'Dejar en blanco para generar automáticamente', lang)} />
                <div className="form-text" style={{ fontSize: 11 }}>
                  {t(`This run's output will be credited to "${recipe.outputIngredientName}" in Ingredient Inventory under this lot code.`,
                    `La salida de esta preparación se acreditará a "${recipe.outputIngredientName}" en el Inventario de Ingredientes bajo este código de lote.`, lang)}
                </div>
              </div>
            </div>
          )}

          {recipe.steps.length > 0 && (
            <div className="card mb-3" style={CARD_STYLE('#fd7e14')}>
              <div className="card-body">
                <div className="text-uppercase text-muted mb-2" style={SECTION_HEADER_STYLE}>📋 {t('Steps', 'Pasos', lang)}</div>
                {recipe.steps.map((s, idx) => (
                  <div key={s.id} className="form-check mb-2">
                    <input type="checkbox" className="form-check-input drp-no-print" id={`step-${s.id}`}
                      checked={!!checkedSteps[s.id]} onChange={() => toggleStep(s.id)} />
                    <label className="form-check-label" htmlFor={`step-${s.id}`}>
                      <span className="text-muted me-1">{idx + 1}.</span>{stepText(s)}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card mb-3 drp-no-print">
            <div className="card-body">
              <label className="form-label small fw-semibold">{t('Notes (optional)', 'Notas (opcional)', lang)}</label>
              <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
          </div>

          <div className="drp-no-print">
            <button className="btn btn-success px-4" onClick={handleSubmit} disabled={!validInput || submitting || !allStepsChecked}>
              {submitting ? t('Logging…', 'Registrando…', lang) : t('✓ Log This Run', '✓ Registrar esta preparación', lang)}
            </button>
            {validInput && !allStepsChecked && (
              <div className="form-text text-warning mt-1">
                {t('Check off every step before logging this run.', 'Marque cada paso antes de registrar esta preparación.', lang)}
              </div>
            )}
          </div>
        </>
      )}

      <hr className="my-4 drp-no-print" />
      <div className="drp-no-print">
        <button className="btn btn-link btn-sm p-0" onClick={loadHistory}>
          {showHistory ? t('Hide run history', 'Ocultar historial', lang) : t('Show run history', 'Mostrar historial', lang)}
        </button>
        {showHistory && (
          <div className="table-responsive mt-2">
            <table className="table table-sm">
              <thead className="table-light"><tr><th>{t('Date', 'Fecha', lang)}</th><th>{t('By', 'Por', lang)}</th><th>{t('Mode', 'Modo', lang)}</th><th className="text-end">{t('Total Flour', 'Harina Total', lang)}</th><th>{t('Output Lot', 'Lote de Salida', lang)}</th><th>{t('Notes', 'Notas', lang)}</th></tr></thead>
              <tbody>
                {history.length === 0 ? (
                  <tr><td colSpan={6} className="text-muted text-center py-3">{t('No runs logged yet.', 'Aún no hay preparaciones registradas.', lang)}</td></tr>
                ) : history.map(h => (
                  <tr key={h.id}>
                    <td>{new Date(h.executedAt).toLocaleString()}</td>
                    <td>{h.executedByName || '—'}</td>
                    <td>{h.inputValueLabel}</td>
                    <td className="text-end">{h.breakdown?.totalFlourG != null ? fmtG(h.breakdown.totalFlourG) : '—'}</td>
                    <td>{h.outputLotCode ? <span className="badge bg-light text-dark border">{h.outputLotCode}</span> : <span className="text-muted">—</span>}</td>
                    <td>{h.notes || <span className="text-muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
